import { Auth, AuthErrorCodes, signInWithCustomToken } from "firebase/auth";
import { getDatabase, onValue, ref } from "firebase/database";
import * as Sentry from "@sentry/react";
import pRetry from "p-retry";

import { FirebaseTokenDocument, FirebaseTokenQuery } from "@/apollo/types";
import type { ApolloClient } from "@apollo/client";

import { getWeldAuth, getWeldFirebaseApp } from "../firebaseApp";

type SignIn = { accountId: string; done: Promise<boolean> };

let latestSignIn: SignIn | undefined;
let signedInAccountId: string | undefined;
let isConnectionWarm = false;
const listeners = new Set<() => void>();

/**
 * Signs the editor's Firebase app in with a token scoped to `accountId`. A sign-in already
 * started or completed for the same account is reused, so the route loader can start it early
 * and the editor picks up the same one. Resolves to whether sign-in succeeded; never rejects.
 */
export function signInToFirebase(
  client: ApolloClient<object>,
  accountId: string,
): Promise<boolean> {
  if (latestSignIn?.accountId === accountId) return latestSignIn.done;

  const auth = getWeldAuth();
  warmUpDatabaseConnection();
  // Workspace switches reload the page today; if one ever doesn't, the old account's listeners must stop before Firebase switches users.
  if (signedInAccountId !== undefined) setSignedInAccountId(undefined);

  const previous = latestSignIn?.done;
  const signIn: SignIn = { accountId, done: Promise.resolve(false) };
  latestSignIn = signIn;
  signIn.done = (async () => {
    const token = await fetchToken(client, accountId);
    // A sign-in for another account still in flight would otherwise overwrite this one's user.
    await previous;
    const succeeded =
      latestSignIn === signIn &&
      token != null &&
      (await signInWithToken(auth, token));
    if (latestSignIn === signIn) {
      if (succeeded) setSignedInAccountId(accountId);
      else latestSignIn = undefined;
    }
    return succeeded;
  })();
  return signIn.done;
}

export function getSignedInAccountId() {
  return signedInAccountId;
}

export function subscribeToSignIn(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function setSignedInAccountId(accountId: string | undefined) {
  signedInAccountId = accountId;
  listeners.forEach((listener) => listener());
}

/**
 * Opens the Realtime Database websocket now instead of on the editor's first listen. It
 * authenticates on its own once sign-in completes; `.info` paths never reach the server.
 */
function warmUpDatabaseConnection() {
  if (isConnectionWarm) return;
  isConnectionWarm = true;
  onValue(ref(getDatabase(getWeldFirebaseApp()), ".info/connected"), () => {});
}

async function fetchToken(client: ApolloClient<object>, accountId: string) {
  try {
    const resp = await client.query<FirebaseTokenQuery>({
      query: FirebaseTokenDocument,
      // The query has no variables, so deduplication would hand one account's token to another.
      context: { accountId, queryDeduplication: false },
      fetchPolicy: "no-cache",
    });
    const token = resp.data?.firebaseToken;
    if (token == null) {
      Sentry.captureException(
        resp.error ??
          new Error("Firebase Token not received - no error response"),
      );
    }
    return token;
  } catch (error) {
    Sentry.captureException(error);
    return undefined;
  }
}

async function signInWithToken(auth: Auth, token: string): Promise<boolean> {
  try {
    await signInRetryingNetworkFailures(auth, token);
    return true;
  } catch (error) {
    Sentry.captureException(error, {
      level: isNetworkRequestFailed(error) ? "warning" : "error",
    });
    return false;
  }
}

function signInRetryingNetworkFailures(auth: Auth, token: string) {
  return pRetry(() => signInWithCustomToken(auth, token), {
    retries: 3,
    shouldRetry: ({ error }) => isNetworkRequestFailed(error),
  });
}

function isNetworkRequestFailed(error: unknown): boolean {
  return (
    error instanceof Error &&
    "code" in error &&
    error.code === AuthErrorCodes.NETWORK_REQUEST_FAILED
  );
}
